import {
  DialogContent,
  type DialogContentProps,
  DialogOverlay,
  DialogPortal,
  DialogTrigger,
  Root as DialogRoot
} from '@radix-ui/react-dialog'
import { useControllableState } from '@radix-ui/react-use-controllable-state'
import clsx from 'clsx'
import React, { useCallback, useMemo } from 'react'

import { Body } from './Body'
import { Button } from './Button'
import { ModalContext } from './context'
import { Description } from './Description'
import { DismissButton } from './DismissButton'
import { Footer } from './Footer'
import { Header } from './Header'
import { Title } from './Title'

interface ModalProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  defaultOpen?: boolean;
  width?: 'small' | 'medium' | 'large' | 'full';
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  trigger?: React.ReactNode;
  zIndex?: number;
  onPointerDownOutside?: DialogContentProps['onPointerDownOutside']
}

function getWidthClassNames (width: ModalProps['width']) {
  let classNames = ''

  switch (width) {
    case 'small':
      classNames = 'xs:max-w-[32rem]'
      break
    case 'medium':
      classNames = 'xs:max-w-[40rem]'
      break
    case 'large':
      classNames = 'xs:max-w-[48rem]'
      break
    case 'full':
      classNames = 'w-full'
      break
  }

  return 'w-full max-h-full ' + classNames
}

export function Modal ({
  children,
  defaultOpen = false,
  onOpenChange,
  open: openProp,
  trigger,
  width = 'small',
  zIndex,
  ...props
}: ModalProps) {
  const [open, setOpen] = useControllableState({
    defaultProp: defaultOpen,
    onChange: onOpenChange,
    prop: openProp,
  })

  const dismissModal = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const contextValue = useMemo(() => ({ dismissModal }), [dismissModal])

  return (
    <DialogRoot open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogPortal>
        {/*
          * This element is a hack to try to force iOS 26 Safari to render a
          * solid tab bar. The default transparency lets overflow content peek
          * out underneath overlays like the mobile side nav and modal dialogs,
          * which looks really bad. Even Apple's own website hacks around this.
          *
          * See GitHub PR #11975 for more context.
          **/}
        {open && (
          <div
            aria-hidden='true'
            className='hidden supports-[-webkit-touch-callout:_none]:block sticky top-0 z-[1000] pointer-events-none'
            data-testid='ios26-safari-tab-bar-hack'
          >
            <div className='fixed top-0 left-0 w-full h-full opacity-0 backdrop-blur-lg' />
          </div>
        )}
        <DialogOverlay
          className='bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in [animation-duration:300ms] fixed inset-0 flex items-center justify-center p-2 xs:p-8 backdrop-blur-sm'
          data-backdrop=''
          style={{ zIndex }}
        >
          <DialogContent
            {...props}
            className={clsx(
              getWidthClassNames(width),
              'flex flex-col',
              'bg-white',
              'rounded-lg shadow-lg',
              'overflow-hidden',
              'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 [animation-duration:300ms]',
              'outline outline-offset-2 outline-2 outline-transparent focus-visible:outline-thatch-500'
            )}
          >
            <ModalContext.Provider value={contextValue}>
              {children}
            </ModalContext.Provider>
          </DialogContent>
        </DialogOverlay>
      </DialogPortal>
    </DialogRoot>
  )
}

Modal.Body = Body
Modal.Button = Button
Modal.Description = Description
Modal.DismissButton = DismissButton
Modal.Footer = Footer
Modal.Header = Header
Modal.Title = Title
